import type { OverviewResponse } from "contracts/api/dashboard/responses";
import type { Issue } from "contracts/api/issue/responses";
import { ORIGIN_RESOURCE_PATHS } from "contracts/api/yeet/origin-subjects";
import { CLIENT_FINDING_FIELD } from "contracts/enums/client-finding";
import { BLOCK_TYPE } from "contracts/enums/metrics";
import {
  clientFacingActionText,
  clientFacingFindingText,
} from "contracts/report/boundaries/client-safe-finding.parser";
import { contentFitnessSubcriterionForKey } from "contracts/report/content-fitness/lookups";
import { metricLabel } from "../website/issueModel";
import {
  clientDisplayText,
  evidenceState,
  isTechnicalEvidence,
  presentEvidence,
} from "./evidencePresentation";
import { currentPageEvidenceForGroup } from "./issuePageEvidence";
import { currentIssuePageGroups } from "./issuePageResolution";
import { siteSpecificText } from "./issueSiteText";

export function clientIssueTitle(issue: Pick<Issue, "title">): string {
  return (
    clientFacingFindingText(
      clientDisplayText(issue.title),
      CLIENT_FINDING_FIELD.TITLE,
    ) ?? "Page-level issue recorded"
  );
}

function rationaleForIssue(issue: Issue): string {
  const note = issue.blocks.find((block) => block.type === BLOCK_TYPE.NOTE);
  if (note?.type === "note") {
    const safe = clientFacingFindingText(
      clientDisplayText(note.body),
      CLIENT_FINDING_FIELD.SUMMARY,
    );
    if (safe) return safe;
  }
  const evidence = issueLevelEvidence(issue, 1)[0];
  return (
    evidence ??
    "The scan recorded this issue on an affected page. Review the page evidence to confirm the observation before acting."
  );
}

function issueLevelEvidence(issue: Issue, limit = 2): string[] {
  return issue.blocks
    .flatMap((block) =>
      block.type === BLOCK_TYPE.EVIDENCE ? [block.body] : [],
    )
    .filter((item) => !isTechnicalEvidence({ value: item }))
    .map(clientDisplayText)
    .slice(0, limit);
}

function recommendationsForIssue(issue: Issue): string[] {
  return issue.blocks.flatMap((block) =>
    block.type === BLOCK_TYPE.RECOMMENDATION
      ? [clientFacingActionText(clientDisplayText(block.body))].filter(
          (value): value is string => value !== null,
        )
      : [],
  );
}

export function siteSpecificRecommendationsForIssue(
  data: OverviewResponse,
  issue: Issue,
): string[] {
  return recommendationsForIssue(issue).filter(
    (recommendation) => siteSpecificText(data, recommendation) !== null,
  );
}

export function suggestedActionForIssueInSite(
  data: OverviewResponse,
  issue: Issue,
): string {
  return (
    siteSpecificRecommendationsForIssue(data, issue)[0] ??
    `Review the page evidence for “${clientIssueTitle(issue)}” and define the appropriate site change.`
  );
}

export function rationaleForIssueInSite(
  data: OverviewResponse,
  issue: Issue,
): string {
  return (
    siteSpecificText(data, rationaleForIssue(issue)) ??
    "This finding is linked to this scan, but its site-specific rationale needs review before changes are made."
  );
}

function evidenceForIssueInSite(
  data: OverviewResponse,
  issue: Issue,
  limit = 2,
): string[] {
  return issueLevelEvidence(issue, limit).filter(
    (item) => siteSpecificText(data, item) !== null,
  );
}

export function issueEvidenceStateForSite(
  data: OverviewResponse,
  issue: Issue,
): string {
  const issueEvidence = presentEvidence(
    evidenceForIssueInSite(data, issue, Number.POSITIVE_INFINITY).map(
      (value) => ({ value }),
    ),
  );
  const pageEvidence = currentIssuePageGroups(data, issue).map((group) => ({
    pageId: group.page.id,
    evidence: currentPageEvidenceForGroup(data, group),
  }));
  const evidencePageCount = pageEvidence.filter(
    ({ evidence }) => evidence.readable.length + evidence.technical.length > 0,
  ).length;
  return evidenceState({
    readableCount:
      issueEvidence.readable.length +
      pageEvidence.reduce(
        (count, { evidence }) => count + evidence.readable.length,
        0,
      ),
    technicalCount:
      issueEvidence.technical.length +
      pageEvidence.reduce(
        (count, { evidence }) => count + evidence.technical.length,
        0,
      ),
    pageCount: evidencePageCount || undefined,
  });
}

export function issueBlocksForSite(
  data: OverviewResponse,
  issue: Issue,
): Issue["blocks"] {
  return issue.blocks.filter((block) => {
    if (
      block.type !== BLOCK_TYPE.NOTE &&
      block.type !== BLOCK_TYPE.EVIDENCE &&
      block.type !== BLOCK_TYPE.RECOMMENDATION
    ) {
      return true;
    }
    return siteSpecificText(data, block.body) !== null;
  });
}

export function rawClientIssueText(
  data: OverviewResponse,
  issue: Issue,
): Array<{ label: string; value: string }> {
  const safeTitle = clientFacingFindingText(
    clientDisplayText(issue.title),
    CLIENT_FINDING_FIELD.TITLE,
  );
  const title =
    safeTitle === issue.title.trim()
      ? []
      : [{ label: "Original generated issue title", value: issue.title }];
  return title.concat(
    issue.blocks.flatMap((block) => {
      if (
        block.type !== BLOCK_TYPE.NOTE &&
        block.type !== BLOCK_TYPE.RECOMMENDATION
      )
        return [];
      const field = block.type === BLOCK_TYPE.NOTE ? "summary" : "action";
      const safe = clientFacingFindingText(
        clientDisplayText(block.body),
        field,
      );
      const siteSpecific = siteSpecificText(data, block.body);
      if (safe === block.body.trim() && siteSpecific !== null) return [];
      return [
        {
          label:
            block.type === BLOCK_TYPE.NOTE
              ? "Original generated finding detail"
              : "Original generated recommendation",
          value: block.body,
        },
      ];
    }),
  );
}

export function issueDimensionLabel(issue: Issue): string {
  if (issue.originResourceKind) {
    return `${metricLabel(issue.metricKey)} / Origin resource ${ORIGIN_RESOURCE_PATHS[issue.originResourceKind]}`;
  }
  const subcriterion = issue.primarySubcriterionKey
    ? contentFitnessSubcriterionForKey(issue.primarySubcriterionKey)
    : null;
  return subcriterion
    ? `${metricLabel(issue.metricKey)} / ${subcriterion.label}`
    : metricLabel(issue.metricKey);
}
